import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight, Bookmark, Check, Search, SlidersHorizontal, X } from "lucide-react";
import { arrayRemove, arrayUnion, collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "../firebase";
import { CompanyLogo } from "./dreamShared";
import {
  ROLE_ROWS,
  applicationFromRole,
  defaultWindow,
  emptyWindow,
  inWindow,
  roleActionDoc,
  roleMeta,
  shownCounts,
  windowCounts,
  windowLabel,
  windowsFor,
  type FeedRole,
  type RoleAction,
  type RoleCounts,
  type RoleScope,
  type RoleWindow,
} from "../utils/newRoles";
import { FRESH_MS, cachedNewRoles, fetchNewRoles, forgetRole, markRoleApplied, targetsKey } from "../utils/newRolesApi";
import {
  NO_FILTERS,
  activeCount,
  dayGroups,
  filterGroups,
  filterRoles,
  hasFacets,
  optionCount,
  toggleFilter,
  type FilterKey,
  type RoleFilters,
} from "../utils/roleFilters";
import { normalizeCompany } from "../utils/dreamCompanies";
import { cn } from "../lib/utils";
import { MELT_BUTTON_WHITE } from "./ui/melt";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import UndoToast, { useUndoToast } from "./UndoToast";

const CARD = "rounded-2xl border border-melt-line bg-melt-paper";
/** The checker adds roles every hour; the card catches up twice as often */
const REFRESH_MS = 30 * 60000;
/** Applied: the check shows this long, then the row slides out */
const CHECK_MS = 650;
const SLIDE_MS = 320;

/** A title compared as-is, ignoring case and spacing (backend new_roles.title_key) */
const titleKey = (title: string) => title.toLowerCase().split(/\s+/).filter(Boolean).join(" ");
const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-2 py-3">
      <span className="h-9 w-9 shrink-0 animate-pulse rounded-[10px] bg-melt-text1/[.06]" />
      <span className="min-w-0 flex-1 space-y-1.5">
        <span className="block h-3.5 w-1/2 animate-pulse rounded bg-melt-text1/[.06]" />
        <span className="block h-3 w-3/4 animate-pulse rounded bg-melt-text1/[.04]" />
      </span>
    </div>
  );
}

const TAG = "whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium";

/** The small labels after the meta line on See all */
function RoleTags({ role }: { role: FeedRole }) {
  const level = role.level === "intern" ? "Internship" : role.level === "new_grad" ? "New grad" : null;
  if (!level && !role.remote && !role.dream) return null;
  return (
    <span className="hidden shrink-0 gap-1 sm:flex">
      {level && <span className={cn(TAG, "bg-melt-track text-melt-text2")}>{level}</span>}
      {role.remote && <span className={cn(TAG, "bg-[#E8EEF3] text-[#3D5568]")}>Remote</span>}
      {role.dream && <span className={cn(TAG, "bg-melt-peach text-melt-amber")}>Dream company</span>}
    </span>
  );
}

/** "check": Applied was pressed, the check shows. "out": sliding away. */
type Leaving = "check" | "out";

function RoleRow({
  role,
  saved,
  leaving,
  full,
  onSave,
  onApplied,
  onDismiss,
}: {
  role: FeedRole;
  saved: boolean;
  leaving?: Leaving;
  /** See all: bigger rows, with tags */
  full: boolean;
  onSave: () => void;
  onApplied: () => void;
  onDismiss: () => void;
}) {
  const added = leaving === "check";
  return (
    <li
      className={cn(
        "grid transition-[grid-template-rows,opacity,transform] duration-300 ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none",
        leaving === "out" ? "grid-rows-[0fr] translate-x-4 opacity-0" : "grid-rows-[1fr]",
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={cn(
            "group relative flex items-center gap-3 rounded-xl transition-colors hover:bg-melt-text1/[.04] [&:has(button:hover)]:bg-transparent",
            full ? "px-2.5 py-3" : "px-1.5 py-2.5",
          )}
        >
          {/* The whole row opens the posting; the buttons sit above the link */}
          <a
            href={role.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open the posting: ${role.title} at ${role.company}`}
            title="Open the posting"
            className="absolute inset-0 rounded-xl"
          />
          <CompanyLogo name={role.company} domain={role.domain || undefined} logo={role.logo} size={full ? 36 : 32} />
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  "truncate font-medium text-melt-text1 decoration-melt-text1/35 underline-offset-[3px] group-hover:underline group-[&:has(button:hover)]:no-underline",
                  full ? "text-[14.5px]" : "text-[14px]",
                )}
                title={role.title}
              >
                {role.title}
              </span>
              <ArrowUpRight
                size={14}
                strokeWidth={2.2}
                aria-hidden="true"
                className="shrink-0 -translate-x-0.5 translate-y-0.5 text-melt-caramel opacity-0 transition-[opacity,transform] duration-200 group-hover:translate-x-0 group-hover:translate-y-0 group-hover:opacity-100 group-[&:has(button:hover)]:opacity-0 [@media(hover:none)]:hidden"
              />
            </span>
            <span className="mt-0.5 flex min-w-0 items-center gap-2">
              <span className="truncate text-[12.5px] leading-snug text-melt-text3">{roleMeta(role)}</span>
              {full && <RoleTags role={role} />}
            </span>
          </span>
          <button
            type="button"
            onClick={onDismiss}
            disabled={!!leaving}
            aria-label={`Not for me: ${role.title}`}
            title="Not for me"
            className="relative grid h-8 w-8 shrink-0 place-items-center rounded-lg text-melt-text3 opacity-0 transition-opacity hover:bg-melt-text1/[.07] hover:text-melt-text1 focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          >
            <X size={15} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!!leaving}
            aria-pressed={saved}
            aria-label={saved ? `Saved: ${role.title}. Click to remove it.` : `Save for later: ${role.title}`}
            title={saved ? "Saved. Click to remove it." : "Save for later"}
            className={cn(
              "relative grid h-9 w-9 shrink-0 place-items-center rounded-[10px] transition-[transform,background-color,color] duration-200 ease-[cubic-bezier(.34,1.56,.64,1)] hover:bg-melt-text1/[.07] active:scale-90",
              saved ? "scale-105 text-melt-caramel" : "text-melt-text3 hover:text-melt-text1",
            )}
          >
            <Bookmark size={18} strokeWidth={1.8} fill={saved ? "currentColor" : "none"} />
          </button>
          <button
            type="button"
            onClick={onApplied}
            disabled={!!leaving}
            title="Add it to your applications"
            className={cn(
              "relative h-9 shrink-0 rounded-[10px] px-3.5 text-[13px] transition-[transform,background-color,border-color,color] active:scale-[.96] disabled:opacity-100",
              added
                ? "inline-flex items-center gap-1.5 border border-melt-caramel bg-melt-caramel font-medium text-white"
                : MELT_BUTTON_WHITE,
            )}
          >
            {added && <Check size={14} strokeWidth={2.4} />}
            {added ? "Added" : "Applied"}
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * See all's filter: an icon like the app's other icon buttons, and a menu
 * like its other menus. Each group is a short list; tapping a row turns it
 * on or off and the list behind updates at once.
 */
function FilterMenu({
  roles,
  query,
  filters,
  saved,
  targets,
  places,
  onChange,
}: {
  roles: FeedRole[];
  query: string;
  filters: RoleFilters;
  saved: Set<string>;
  targets: string[];
  places: string[];
  onChange: (filters: RoleFilters) => void;
}) {
  const n = activeCount(filters);
  const groups = filterGroups(targets, places, hasFacets(roles));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={n ? `Filters, ${n} on` : "Filters"}
          title="Filters"
          className={cn(
            "relative grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors hover:bg-melt-text1/[.07] data-[state=open]:bg-melt-text1/[.07]",
            n ? "text-melt-caramel" : "text-melt-text3 hover:text-melt-text1",
          )}
        >
          <SlidersHorizontal size={17} strokeWidth={1.75} />
          {n > 0 && (
            <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-melt-caramel px-1 font-plex text-[10px] font-semibold text-white">
              {n}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        collisionPadding={16}
        className="max-h-[var(--radix-popover-content-available-height)] w-60 overflow-y-auto rounded-[11px] border-melt-hair bg-melt-menu p-[5px] font-grotesk text-melt-text1 shadow-[0_18px_34px_-18px_rgba(92,52,23,.5)]"
      >
        {groups.map((group, i) => (
          <div key={group.key} role="group" aria-label={group.label}>
            {i > 0 && <div className="mx-1 my-1 h-px bg-melt-hair" />}
            <p className="px-2.5 pb-1 pt-1.5 font-plex text-[10.5px] uppercase tracking-[.08em] text-melt-text3">
              {group.label}
            </p>
            {group.options.map((option) => {
              const on = filters[group.key].includes(option.value);
              const count = optionCount(roles, query, filters, saved, group.key as FilterKey, option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onChange(toggleFilter(filters, group.key, option.value))}
                  className="flex w-full items-center gap-2 rounded-[7px] px-2.5 py-[7px] text-left text-[13px] font-medium transition-colors hover:bg-melt-text1/[.06]"
                >
                  <span className="grid w-3.5 shrink-0 place-items-center text-melt-caramel">
                    {on && <Check size={14} strokeWidth={2.2} />}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  <span className="font-plex text-[11px] text-melt-text3">{count.toLocaleString()}</span>
                </button>
              );
            })}
          </div>
        ))}
        {n > 0 && (
          <>
            <div className="mx-1 my-1 h-px bg-melt-hair" />
            <button
              type="button"
              onClick={() => onChange(NO_FILTERS)}
              className="flex w-full items-center rounded-[7px] px-2.5 py-[7px] pl-8 text-left text-[13px] font-medium text-melt-text2 transition-colors hover:bg-melt-text1/[.06] hover:text-melt-text1"
            >
              Clear all
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * New roles for you: fresh postings matched to the user (utils/newRoles.ts).
 * On Home it shows the top 5 with "See all"; the See all page passes
 * `limit` to show every one, and scope "all" for every open role of any age
 * (Today / 3 days / Week / Month / All), with search, filters and the roles
 * grouped by day.
 */
export default function NewRolesCard({
  user,
  onEditRoles,
  onSeeAll,
  limit = ROLE_ROWS,
  initialWindow,
  scope = "new",
}: {
  user: User | null;
  onEditRoles: () => void;
  /** Leave out to hide the footer (the See all page) */
  onSeeAll?: (window: RoleWindow) => void;
  limit?: number;
  initialWindow?: RoleWindow;
  scope?: RoleScope;
}) {
  const full = scope === "all";
  const windows = windowsFor(scope);
  // null until the first load
  const [roles, setRoles] = useState<FeedRole[] | null>(null);
  // Over every match, not just the roles sent (See all gets the best 1,000)
  const [serverCounts, setServerCounts] = useState<RoleCounts | undefined>();
  const [capped, setCapped] = useState<RoleWindow[] | undefined>();
  const [failed, setFailed] = useState(false);
  // A fresh copy is loading (the saved one shows meanwhile)
  const [refreshing, setRefreshing] = useState(false);
  const [reload, setReload] = useState(0);
  const [actions, setActions] = useState<Record<string, RoleAction>>({});
  // Applied or hidden on this visit: gone at once, before the next load drops them
  const [gone, setGone] = useState<Set<string>>(new Set());
  const [leaving, setLeaving] = useState<Record<string, Leaving>>({});
  const [picked, setPicked] = useState<RoleWindow | null>(initialWindow ?? null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<RoleFilters>(NO_FILTERS);
  // Re-render each minute so "2h ago" and the windows stay current
  const [now, setNow] = useState(Date.now());
  // The job search settings roles are matched on (targetsKey); null until read
  const [targets, setTargets] = useState<string | null>(null);
  // Their target roles and locations, for the filters and the summary line
  const [prefs, setPrefs] = useState<{ roles: string[]; locations: string[] }>({ roles: [], locations: [] });
  const { toast, show: showToast, hide: hideToast } = useUndoToast();
  // Hidden with "Not this company" / "Not this role" (users/{uid}.hiddenCompanies / hiddenRoles). The
  // backend leaves them out too, and roles that mean nearly the same; here, the same company or title
  const [hiddenCompanies, setHiddenCompanies] = useState<string[]>([]);
  const [hiddenRoles, setHiddenRoles] = useState<string[]>([]);

  useEffect(() => {
    if (!user) {
      setTargets(null);
      return;
    }
    return onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        setTargets(targetsKey(snap.data()));
        setPrefs({ roles: strings(snap.data()?.roles), locations: strings(snap.data()?.locations) });
      },
      (error) => {
        console.error("Could not read job search settings:", error);
        setTargets(targetsKey(undefined));
      },
    );
  }, [user]);

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!user || targets === null) {
      setRoles(null);
      return;
    }
    let live = true;
    // Show the last roles at once (only if matched for these settings); load
    // fresh ones unless those are only a moment old
    const cached = cachedNewRoles(user.uid, scope, targets);
    setRoles(cached ? cached.roles : null);
    setServerCounts(cached?.counts);
    setCapped(cached?.capped);
    setFailed(false);
    setRefreshing(false);
    const load = () => {
      setRefreshing(true);
      return fetchNewRoles(scope, targets)
        .then((next) => {
          if (!live) return;
          setRoles(next.roles);
          setServerCounts(next.counts);
          setCapped(next.capped);
          setFailed(false);
          setNow(Date.now());
        })
        .catch((e) => {
          console.error("Could not load new roles:", e);
          if (live) setFailed(true);
        })
        .finally(() => live && setRefreshing(false));
    };
    // Only while the tab is on screen: each refresh can wake the backend, and
    // a tab left open in the background woke it all day
    let loadedAt = Date.now();
    const refresh = () => {
      if (document.hidden || Date.now() - loadedAt < REFRESH_MS) return;
      loadedAt = Date.now();
      load();
    };
    if (!cached || reload > 0 || cached.ageMs > FRESH_MS) load();
    else loadedAt = Date.now() - cached.ageMs;
    const every = window.setInterval(refresh, REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      window.clearInterval(every);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [user, reload, scope, targets]);

  useEffect(() => {
    if (!user) {
      setActions({});
      return;
    }
    return onSnapshot(
      collection(db, "users", user.uid, "roleActions"),
      (snapshot) => {
        const next: Record<string, RoleAction> = {};
        snapshot.docs.forEach((d) => (next[d.id] = { id: d.id, ...d.data() } as RoleAction));
        setActions(next);
      },
      (error) => console.error("Could not load saved roles:", error),
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setHiddenCompanies([]);
      setHiddenRoles([]);
      return;
    }
    return onSnapshot(
      doc(db, "users", user.uid),
      (snapshot) => {
        setHiddenCompanies(strings(snapshot.data()?.hiddenCompanies));
        setHiddenRoles(strings(snapshot.data()?.hiddenRoles));
      },
      (error) => console.error("Could not load hidden companies:", error),
    );
  }, [user]);

  const [visible, hidden] = useMemo(() => {
    const hiddenKeys = new Set(hiddenCompanies.map(normalizeCompany));
    const hiddenTitles = new Set(hiddenRoles.map(titleKey));
    // A row sliding out stays until it's gone
    const keep = (r: FeedRole) =>
      !!leaving[r.id] ||
      (actions[r.id]?.status !== "dismissed" &&
        !gone.has(r.id) &&
        !hiddenKeys.has(normalizeCompany(r.company)) &&
        !hiddenTitles.has(titleKey(r.title)));
    return [(roles || []).filter(keep), (roles || []).filter((r) => !keep(r))];
  }, [roles, actions, gone, leaving, hiddenCompanies, hiddenRoles]);
  const saved = useMemo(
    () => new Set(Object.values(actions).filter((a) => a.status === "saved").map((a) => a.id)),
    [actions],
  );
  const searching = full && (query.trim() !== "" || activeCount(filters) > 0);
  const matching = useMemo(
    () => (searching ? filterRoles(visible, query, filters, saved) : visible),
    [searching, visible, query, filters, saved],
  );
  const counts = useMemo(
    () => (searching ? windowCounts(matching, now, windows) : shownCounts(visible, hidden, serverCounts, now, windows, capped)),
    [searching, matching, visible, hidden, serverCounts, now, windows, capped],
  );
  // Picked once roles are in, so dismissing a row doesn't switch the window under them
  useEffect(() => {
    if (roles && picked === null) setPicked(scope === "all" ? "all" : defaultWindow(windowCounts(roles, Date.now())));
  }, [roles, picked, scope]);
  const current = picked ?? "today";
  const shown = useMemo(() => inWindow(matching, current, now), [matching, current, now]);
  const loading = roles === null && !failed;

  const actionRef = (id: string) => (user ? doc(db, "users", user.uid, "roleActions", id) : null);
  const setGoneFor = (id: string, on: boolean) =>
    setGone((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const setLeavingFor = (id: string, state: Leaving | null) =>
    setLeaving((prev) => {
      const next = { ...prev };
      if (state) next[id] = state;
      else delete next[id];
      return next;
    });

  const save = (role: FeedRole) => {
    const ref = actionRef(role.id);
    if (!ref) return;
    if (saved.has(role.id)) {
      deleteDoc(ref).catch((e) => console.error("Could not unsave role:", e));
      return;
    }
    setDoc(ref, { ...roleActionDoc(role, "saved"), at: serverTimestamp() })
      .then(() =>
        showToast({
          title: "Saved to Drafts",
          sub: role.company,
          undo: () => {
            hideToast();
            deleteDoc(ref).catch((e) => console.error("Could not unsave role:", e));
          },
        }),
      )
      .catch((e) => console.error("Could not save role:", e));
  };

  const dismiss = (role: FeedRole) => {
    const ref = actionRef(role.id);
    if (!ref || leaving[role.id]) return;
    setLeavingFor(role.id, "out");
    window.setTimeout(() => {
      setGoneFor(role.id, true);
      setLeavingFor(role.id, null);
    }, SLIDE_MS);
    setDoc(ref, { ...roleActionDoc(role, "dismissed"), at: serverTimestamp() })
      .then(() =>
        showToast({
          title: "Hidden",
          sub: "You won’t see this role again",
          undo: () => {
            hideToast();
            setGoneFor(role.id, false);
            deleteDoc(ref).catch((e) => console.error("Could not show role again:", e));
          },
          ask: "Why?",
          choices: [
            // Not for a Dream Company: hiding the place they want to work by accident costs too much
            ...(role.dream ? [] : [{ label: "Not this company", pick: () => hideCompany(role) }]),
            { label: "Not this role", pick: () => hideRole(role) },
            { label: "Already applied", pick: () => alreadyApplied(role) },
          ],
        }),
      )
      .catch((e) => {
        console.error("Could not hide role:", e);
        setGoneFor(role.id, false);
      });
  };

  /** Why a role was hidden, kept on its roleActions doc */
  const keepReason = (role: FeedRole, reason: "company" | "role" | "applied") => {
    const ref = actionRef(role.id);
    if (ref) setDoc(ref, { reason }, { merge: true }).catch((e) => console.error("Could not save why:", e));
  };

  const hideCompany = (role: FeedRole) => {
    if (!user) return;
    keepReason(role, "company");
    const userRef = doc(db, "users", user.uid);
    setDoc(userRef, { hiddenCompanies: arrayUnion(role.company) }, { merge: true })
      .then(() =>
        showToast({
          title: `You won’t see ${role.company} roles`,
          sub: "Show them again in Settings",
          undo: () => {
            hideToast();
            setDoc(userRef, { hiddenCompanies: arrayRemove(role.company) }, { merge: true }).catch((e) =>
              console.error("Could not show the company again:", e),
            );
          },
        }),
      )
      .catch((e) => console.error("Could not hide the company:", e));
  };

  const hideRole = (role: FeedRole) => {
    if (!user) return;
    keepReason(role, "role");
    const userRef = doc(db, "users", user.uid);
    setDoc(userRef, { hiddenRoles: arrayUnion(role.title) }, { merge: true })
      .then(() =>
        showToast({
          title: "You’ll see fewer roles like this",
          sub: role.title,
          undo: () => {
            hideToast();
            setDoc(userRef, { hiddenRoles: arrayRemove(role.title) }, { merge: true }).catch((e) =>
              console.error("Could not show the role again:", e),
            );
          },
        }),
      )
      .catch((e) => console.error("Could not hide the role:", e));
  };

  const alreadyApplied = (role: FeedRole) => {
    if (!user) return;
    keepReason(role, "applied");
    markRoleApplied(applicationFromRole(role))
      .then((result) => {
        forgetRole(role.id);
        showToast({
          title: "Added to your applications",
          sub: result.merged ? `Joined your ${role.company} card` : `${role.company} · ${role.title}`,
          undo: result.merged
            ? undefined
            : () => {
                hideToast();
                deleteDoc(doc(db, "users", user.uid, "emails", result.cardId)).catch((e) =>
                  console.error("Could not undo applied:", e),
                );
              },
        });
      })
      .catch((e) => {
        console.error("Could not add application:", e);
        showToast({ title: `Couldn’t add ${role.company}`, sub: "Please try again." });
      });
  };

  const markApplied = (role: FeedRole) => {
    if (!user || leaving[role.id]) return;
    const wasSaved = saved.has(role.id);
    setLeavingFor(role.id, "check");
    window.setTimeout(() => setLeavingFor(role.id, "out"), CHECK_MS);
    window.setTimeout(() => {
      setGoneFor(role.id, true);
      setLeavingFor(role.id, null);
    }, CHECK_MS + SLIDE_MS);
    // Applied now, so it isn't waiting in saved roles any more
    const ref = actionRef(role.id);
    if (ref && wasSaved) deleteDoc(ref).catch((e) => console.error("Could not update saved role:", e));
    Promise.all([markRoleApplied(applicationFromRole(role)), wait(CHECK_MS + SLIDE_MS)])
      .then(([result]) => {
        forgetRole(role.id);
        showToast({
          title: "Added to your applications",
          sub: result.merged ? `Joined your ${role.company} card` : `${role.company} · ${role.title}`,
          // Joined an existing card: nothing of ours to take back
          undo: result.merged
            ? undefined
            : () => {
                hideToast();
                setGoneFor(role.id, false);
                deleteDoc(doc(db, "users", user.uid, "emails", result.cardId)).catch((e) =>
                  console.error("Could not undo applied:", e),
                );
                if (ref && wasSaved)
                  setDoc(ref, { ...roleActionDoc(role, "saved"), at: serverTimestamp() }).catch((e) =>
                    console.error("Could not save role again:", e),
                  );
              },
        });
      })
      .catch((e) => {
        console.error("Could not add application:", e);
        setGoneFor(role.id, false);
        setLeavingFor(role.id, null);
        showToast({ title: `Couldn’t add ${role.company}`, sub: "Please try again." });
      });
  };

  const empty = emptyWindow(current, windows);
  const widest = windows[windows.length - 1].id;
  const rowsShown = shown.slice(0, limit);
  const row = (role: FeedRole) => (
    <RoleRow
      key={role.id}
      role={role}
      full={full}
      saved={saved.has(role.id)}
      leaving={leaving[role.id]}
      onSave={() => save(role)}
      onApplied={() => markApplied(role)}
      onDismiss={() => dismiss(role)}
    />
  );

  const tabs = (
    <div
      className={cn("flex rounded-[10px] bg-melt-text1/[.05] p-1", full && "h-11 rounded-xl")}
      role="tablist"
      aria-label="Posted within"
    >
      {windows.map(({ id, label }) => {
        const n = counts[id] ?? 0;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={current === id}
            disabled={!loading && n === 0 && current !== id}
            onClick={() => setPicked(id)}
            className={cn(
              "rounded-[7px] px-2.5 py-1 text-[13px] font-medium transition-colors disabled:cursor-default disabled:opacity-40",
              full && "rounded-[9px]",
              current === id
                ? "bg-white text-melt-text1 shadow-[0_1px_2px_rgba(92,52,23,.08)]"
                : "text-melt-text2 enabled:hover:text-melt-text1",
            )}
          >
            {label}
            {!loading && <span className="ml-1 font-plex text-[11px] text-melt-text3">{n}</span>}
          </button>
        );
      })}
    </div>
  );

  const retry = (
    <button
      type="button"
      onClick={() => setReload((n) => n + 1)}
      className="font-medium text-melt-text1 underline underline-offset-2"
    >
      Try again
    </button>
  );

  const clearAll = () => {
    setQuery("");
    setFilters(NO_FILTERS);
  };

  const list = loading ? (
    <div aria-label="Loading new roles">
      <SkeletonRow />
      <SkeletonRow />
      <SkeletonRow />
    </div>
  ) : failed && !roles ? (
    <p className="px-1 py-3 text-[13.5px] text-melt-text3">Couldn’t load new roles. {retry}</p>
  ) : !counts[widest] && !searching ? (
    <div className="px-1 py-3 text-[13.5px] leading-relaxed text-melt-text2">
      {full ? "No open roles fit you yet." : "No new roles this week."} We check every hour.
      <button
        type="button"
        onClick={onEditRoles}
        className="mt-2 block font-medium text-melt-text1 underline underline-offset-2"
      >
        Edit target roles
      </button>
    </div>
  ) : shown.length === 0 && searching ? (
    <div className="px-2 py-6">
      <p className="text-[15px] font-semibold text-melt-text1">No roles match</p>
      <p className="mt-1 text-[13px] text-melt-text2">
        Try fewer filters or a shorter search.{" "}
        <button type="button" onClick={clearAll} className="font-medium text-melt-text1 underline underline-offset-2">
          Clear search and filters
        </button>
      </p>
    </div>
  ) : shown.length === 0 ? (
    <p className="px-1 py-3 text-[13.5px] text-melt-text3">
      {empty.text}{" "}
      {empty.next && (
        <button
          type="button"
          onClick={() => setPicked(empty.next)}
          className="font-medium text-melt-text1 underline-offset-2 hover:underline"
        >
          Try {windowLabel(empty.next)}.
        </button>
      )}
    </p>
  ) : full ? (
    dayGroups(rowsShown, now).map((group) => (
      <section key={group.label}>
        <h3 className="px-2.5 pb-1 pt-3 font-plex text-[11px] font-medium uppercase tracking-[.06em] text-melt-text3">
          {group.label} <span className="text-melt-text3/80">· {group.roles.length}</span>
        </h3>
        <ul>{group.roles.map(row)}</ul>
      </section>
    ))
  ) : (
    <ul>{rowsShown.map(row)}</ul>
  );

  // Only once there's something on screen: before that the skeleton says it
  const updating = refreshing && !!roles && !failed && (
    <span className="animate-pulse text-[12px] font-normal text-melt-text3">Updating…</span>
  );
  const failedNote = failed && roles && (
    <p className="mt-2 text-[12.5px] text-melt-text3">Couldn’t refresh: these may be out of date. {retry}</p>
  );

  if (full) {
    const named = prefs.roles.slice(0, 2).join(", ");
    const more = prefs.roles.length - 2;
    const total = counts[current] ?? 0;
    return (
      <div className="flex flex-col gap-4">
        <div>
          {/* The page title is in the top bar (pages/NewRoles.tsx) */}
          <p className="truncate text-[13.5px] text-melt-text2">
            {!loading && `${total.toLocaleString()} ${total === 1 ? "role" : "roles"} · `}
            {named ? `matched to ${named}${more > 0 ? ` +${more}` : ""} · ` : ""}
            <button type="button" onClick={onEditRoles} className="text-melt-text3 underline underline-offset-2 hover:text-melt-text1">
              Edit
            </button>
            {updating && <> · {updating}</>}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <label className="flex h-11 min-w-0 flex-[1_1_280px] items-center gap-2.5 rounded-xl border border-melt-line bg-melt-paper pl-3.5 pr-1.5 transition-shadow focus-within:border-melt-caramel focus-within:shadow-[0_0_0_3px_rgba(217,136,41,.15)]">
            <Search size={17} strokeWidth={1.9} className="shrink-0 text-melt-text3" aria-hidden="true" />
            <span className="sr-only">Search roles</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title or company"
              className="min-w-0 flex-1 bg-transparent text-[14.5px] text-melt-text1 outline-none placeholder:text-melt-text3 [&::-webkit-search-cancel-button]:hidden"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="grid h-8 w-8 place-items-center rounded-lg text-melt-text3 transition-colors hover:bg-melt-text1/[.07] hover:text-melt-text1"
              >
                <X size={14} strokeWidth={2} />
              </button>
            )}
            {/* The filter sits at the end of the search bar */}
            <FilterMenu
              roles={inWindow(visible, current, now)}
              query={query}
              filters={filters}
              saved={saved}
              targets={prefs.roles}
              places={prefs.locations}
              onChange={setFilters}
            />
          </label>
          {tabs}
        </div>

        {activeCount(filters) > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {filterGroups(prefs.roles, prefs.locations, hasFacets(visible)).flatMap((group) =>
              group.options
                .filter((option) => filters[group.key].includes(option.value))
                .map((option) => (
                  <button
                    key={`${group.key}:${option.value}`}
                    type="button"
                    onClick={() => setFilters(toggleFilter(filters, group.key, option.value))}
                    aria-label={`Remove filter: ${option.label}`}
                    className="inline-flex h-[30px] items-center gap-1.5 rounded-full border border-melt-caramel bg-melt-peach pl-3 pr-2 text-[13px] font-medium text-melt-amber transition-colors hover:bg-[#F8E6CC]"
                  >
                    {option.label}
                    <X size={12} strokeWidth={2.2} />
                  </button>
                )),
            )}
            <button
              type="button"
              onClick={() => setFilters(NO_FILTERS)}
              className="px-1 py-1.5 text-[13px] font-medium text-melt-text2 underline underline-offset-[3px] hover:text-melt-text1"
            >
              Clear all
            </button>
          </div>
        )}

        {failedNote}
        <section className={cn(CARD, "px-2 pb-2 pt-1")}>{list}</section>

        {/* See all sends the best 1,000: say when a window has more */}
        {!loading && !searching && total > shown.length && shown.length > 0 && (
          <p className="px-1 text-[12.5px] text-melt-text3">
            Showing the best {shown.length.toLocaleString()} of {total.toLocaleString()}.
          </p>
        )}
        <p className="px-1 text-[12.5px] text-melt-text3">Saved roles wait for you in Drafts.</p>
        <UndoToast toast={toast} />
      </div>
    );
  }

  return (
    <section className={cn(CARD, "px-4 pb-3.5 pt-[18px]")}>
      <div className="flex flex-wrap items-center justify-between gap-3 px-1.5">
        <h2 className="flex items-baseline gap-2 text-[16px] font-semibold text-melt-text1">
          New roles for you
          {updating}
        </h2>
        {tabs}
      </div>
      {failedNote}
      <div className="mt-2.5">{list}</div>

      <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-melt-line px-1.5 pt-2">
        <span className="truncate text-[12.5px] text-melt-text3">
          {saved.size ? `${saved.size} saved · in Drafts` : "Save roles to apply later"}
        </span>
        {/* Only when some roles don't fit on the card */}
        {onSeeAll && !loading && shown.length > limit && (
          <button
            type="button"
            onClick={() => onSeeAll(current)}
            className="shrink-0 py-1 text-[13px] font-medium text-melt-text1 hover:underline hover:underline-offset-[3px]"
          >
            See all {(counts[current] ?? shown.length).toLocaleString()} →
          </button>
        )}
      </div>
      <UndoToast toast={toast} />
    </section>
  );
}
